import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { getBusinessRelationsForUser } from 'lib/api';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import BusinessInput from 'lib/common/BusinessInput';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import { validaRut } from 'lib/functions/utils';
import { SubProductCreditLineConcentration } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DetailedRiskPremiumCalculator from './DetailedRiskPremiumCalculator';
import WarrantyCreationConfirmation from './WarrantyCreationConfirmation';

type SubProduct = SubProductCreditLineConcentration;

interface Business {
  identifier: string;
  businessName: string;
}

interface CreateWarrantyDialogProps {
  open: boolean;
  onClose: () => void;
  fetchWarranties: () => void;
  initialValues?: WarrantyFormData;
}

export interface WarrantyFormData {
  type: string;
  quotedAmount: string;
  quotedCurrency: 'UF' | 'USD' | 'CLP';
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
  unitCode?: string;
  business: Business | null;
  description?: string;
}

export interface RiskPremiumRates {
  variableRate?: number;
  fixedRate?: number;
  variableDiscount?: number;
  fixedDiscount?: number;
}

const BASE_WARRANTY = {
  type: '',
  quotedAmount: '',
  quotedCurrency: 'CLP' as 'UF' | 'USD' | 'CLP',
  startDate: null,
  endDate: null,
  tenderId: '',
  principalRut: '',
  unitCode: '',
  business: null,
  description: '',
};

const useCreditLineData = (businessIdentifier: string | undefined) => {
  const dispatch = useDispatch();
  const [subProductsData, setSubProductsData] = useState<SubProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCreditLineData = async () => {
      try {
        setIsLoading(true);
        if (!businessIdentifier) {
          setSubProductsData([]);
          return;
        }
        const creditLine = await getCreditLineByProduct(
          dispatch,
          businessIdentifier,
          'sgr',
        );
        const data = creditLine?.subProducts || creditLine || [];
        setSubProductsData(data);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchCreditLineData();
  }, [businessIdentifier, dispatch]);

  return {
    subProductsData,
    isLoading,
    error,
  };
};

const CreateWarrantyDialog: React.FC<CreateWarrantyDialogProps> = ({
  open,
  onClose,
  fetchWarranties,
  initialValues,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<WarrantyFormData>(BASE_WARRANTY);
  const [daysDifference, setDaysDifference] = useState<number>(0);
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: undefined,
    fixedRate: undefined,
    variableDiscount: undefined,
    fixedDiscount: undefined,
  });
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);

  const { subProductsData } = useCreditLineData(formData.business?.identifier);

  const setUpBusinesses = async () => {
    try {
      const results = await getBusinessRelationsForUser(dispatch);
      if (results) setBusinesses(results.relations);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, quotedAmount: value }));
  };

  const updateFormData = (updates: Partial<WarrantyFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleOpenConfirmation = () => {
    if (isFormValid()) {
      setConfirmationOpen(true);
    }
  };

  const formatBusinessLabel = (business: Business) => {
    if (!business.businessName) return business.identifier;
    return `${business.businessName} - ${business.identifier}`;
  };

  const handleClose = () => {
    setConfirmationOpen(false);
    onClose();
  };

  useEffect(() => {
    void setUpBusinesses();
  }, []);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = formData.startDate.toDate();
      const end = formData.endDate.toDate();
      const days = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );
      setDaysDifference(days);
    } else {
      setDaysDifference(0);
    }
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      if (formData.endDate.isBefore(formData.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    if (formData.principalRut && !validaRut(formData.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [formData.principalRut]);

  useEffect(() => {
    if (!formData.business) {
      setBusinessError('Debe seleccionar una empresa');
    } else {
      setBusinessError(null);
    }
  }, [formData.business]);

  useEffect(() => {
    if (initialValues) setFormData(initialValues);
  }, [initialValues, open]);

  const isFormValid = () => {
    return (
      formData.type &&
      formData.quotedAmount &&
      formData.quotedCurrency &&
      formData.startDate &&
      formData.endDate &&
      formData.principalRut &&
      formData.business &&
      !rutError &&
      !dateError &&
      !businessError
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <CustomDialogTitle setOpen={onClose}>
            Nueva Garantía SGR
          </CustomDialogTitle>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={businesses}
                    getOptionLabel={(option) => formatBusinessLabel(option)}
                    isOptionEqualToValue={(option, value) =>
                      option?.identifier === value?.identifier
                    }
                    value={formData.business}
                    onChange={(event, newValue) => {
                      updateFormData({ business: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        error={!!businessError}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ID Licitación"
                    value={formData.tenderId}
                    onChange={(e) =>
                      updateFormData({ tenderId: e.target.value })
                    }
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de Garantía"
                    value={formData.type}
                    onChange={(e) => updateFormData({ type: e.target.value })}
                    required
                  >
                    {SGR_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Monto Cotizado"
                    type="number"
                    value={parseFloat(formData.quotedAmount) || ''}
                    onChange={handleAmountChange}
                    inputProps={{ step: 'any' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.quotedCurrency}
                        </InputAdornment>
                      ),
                    }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Moneda"
                    value={formData.quotedCurrency}
                    onChange={(e) =>
                      updateFormData({
                        quotedCurrency: e.target.value as 'UF' | 'USD' | 'CLP',
                      })
                    }
                    required
                  >
                    {CURRENCIES.map((currency) => (
                      <MenuItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Fecha Inicio"
                      value={formData.startDate}
                      onChange={(date) => updateFormData({ startDate: date })}
                      minDate={dayjs().subtract(3, 'day')}
                      maxDate={dayjs().add(7, 'day')}
                      slotProps={{
                        textField: { fullWidth: true, required: true },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Fecha Término"
                      value={formData.endDate}
                      onChange={(date) => updateFormData({ endDate: date })}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!dateError,
                          helperText: dateError,
                          required: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {!dateError && daysDifference !== 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Duración de la garantía: {daysDifference} días
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <BusinessInput
                    key={formData.principalRut}
                    identifier={formData.principalRut}
                    unitCode={formData.unitCode}
                    onChange={(principalRut, unitCode) =>
                      updateFormData({ principalRut, unitCode })
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Glosa"
                    value={formData.description ?? ''}
                    onChange={(e) =>
                      updateFormData({ description: e.target.value })
                    }
                    multiline
                    minRows={2}
                    maxRows={2}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <DetailedRiskPremiumCalculator
                amount={parseFloat(formData.quotedAmount) || 0}
                startDate={formData.startDate?.toISOString() ?? ''}
                endDate={formData.endDate?.toISOString() ?? ''}
                quotedCurrency={formData.quotedCurrency}
                type={formData.type}
                businessIdentifier={formData.business?.identifier ?? null}
                rateControls={rateControls}
                setRateControls={setRateControls}
                useConcentrationRates={true}
                canEditRates={true}
                isCreating={true}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <CustomButton color="secondary" onClick={onClose}>
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleOpenConfirmation}
            disabled={!isFormValid()}
          >
            Crear Garantía
          </CustomButton>
        </Box>
      </Dialog>
      {isConfirmationOpen && (
        <WarrantyCreationConfirmation
          open={isConfirmationOpen}
          formData={formData}
          rateControls={rateControls}
          subProductsData={subProductsData}
          fetchWarranties={fetchWarranties}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default CreateWarrantyDialog;
